import { Checkbox, Text } from '@radix-ui/themes'
import Head from 'next/head'
import { signIn as authenticate, getCsrfToken } from 'next-auth/react'
import { Title as DialogTitle } from '../../components/dialog'
import * as Form from '../../components/form'
import { Link } from '../../components/link'
import * as TextField from '../../components/text-field'
import { API, useService } from '../../hooks/use-api'

import styles from './auth.module.css'
import { Actions } from './utils'
import { EmailValidation, PasswordValidation } from './validation'

export { SignUpPage as Page }

function SignUpPage({ routes }) {
  const { callPost: callSignup } = useService(API('/auth/signup'))

  return (
    <>
      <Head>
        <title>Sign up</title>
      </Head>

      <DialogTitle className={styles.heading}>Create account</DialogTitle>

      <Form.Root action={createOrganization}>
        <Form.Field name="organization" required>
          <Form.Label>
            <Text>Organization</Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root autoComplete="off" placeholder="Acme Corporation" />
          </Form.Control>
        </Form.Field>

        <Form.Field name="fullname" required>
          <Form.Label>
            <Text>Full name</Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root placeholder="Alice Smith" required />
          </Form.Control>
        </Form.Field>

        <Form.Field name="email" required>
          <Form.Label>
            <Text>E-mail</Text>
          </Form.Label>
          {...EmailValidation.messages}
          <Form.Control asChild>
            <TextField.Root
              {...EmailValidation.rules}
              autoComplete="username"
              placeholder="alice@company.com"
              type="email"
            />
          </Form.Control>
        </Form.Field>

        <Form.Field name="password" required>
          <Form.Label>
            <Text>Password</Text>
          </Form.Label>
          {...PasswordValidation.messages}
          <Form.Control asChild>
            <TextField.Root
              {...PasswordValidation.rules}
              autoComplete="new-password"
              placeholder="••••••••"
              type="password"
            />
          </Form.Control>
        </Form.Field>

        <Form.Field name="eula" required>
          <Form.Control asChild>
            <Checkbox value="agree" />
          </Form.Control>
          <Form.Label>
            Agree to the{' '}
            <Link
              href="/static/legal/data-privacy-agreement-20230928.pdf"
              target="_blank"
            >
              Data Privacy Agreement
            </Link>
            {' and '}
            <Link
              href="/static/legal/eval-agreement-20231113.pdf"
              target="_blank"
            >
              Software Evaluation Agreement
            </Link>
          </Form.Label>
        </Form.Field>

        <Form.Submit>Create account</Form.Submit>

        {!!routes[Actions.SignIn] && (
          <Text align="center" mt="2" size="1">
            Already have an account?{' '}
            <Link
              href={{
                pathname: routes[Actions.SignIn],
                query: {
                  organizationId: '',
                },
              }}
            >
              Sign in
            </Link>
          </Text>
        )}
      </Form.Root>
    </>
  )

  /** @type {import('../../components/form').FormAction} */
  async function createOrganization(formData) {
    const { email, password, ...formEntries } = Object.fromEntries(formData)
    const csrfToken = await getCsrfToken()

    const { result, error } = await callSignup({
      body: {
        ...formEntries,
        email,
        password,
        csrfToken,
      },
    })

    if (error) {
      throw error
    }

    await authenticate('credentials', {
      redirect: true,
      callbackUrl: '/',
      organizationId: result.organization.id,
      username: email,
      password,
    })
  }
}
